/* eslint-disable no-restricted-globals */
import {
  ElementInteractionsOptions,
  ActionType,
  getDecodeURI,
  IDiagnosticsClient,
  MASKED_TEXT_VALUE,
  TEXT_MASK_ATTRIBUTE,
  getPageTitle,
  replaceSensitiveString,
} from '@amplitude/analytics-core';
import type { DataSource } from '@amplitude/analytics-core/lib/esm/types/element-interactions';
import * as constants from './constants';
import {
  removeEmptyProperties,
  extractPrefixedAttributes,
  isElementPointerCursor,
  getClosestElement,
  isElementBasedEvent,
  parseAttributesToMask,
  getCurrentPageViewId,
} from './helpers';
import type { BaseTimestampedEvent, ElementBasedTimestampedEvent, TimestampedEvent, JSONValue } from './helpers';
import { getAncestors, getElementProperties } from './hierarchy';
import { getDataSource } from './pageActions/actions';
import { Hierarchy } from './typings/autocapture';
import {
  createSelectorEngine,
  resolveSelectorConfig,
  type SelectorEngine,
  type ElementSelectorRemoteConfig,
  type ElementSelectorLogger,
} from '@amplitude/element-selector';

/**
 * Module-level shared selector engine singleton. Both autocapture-plugin and
 * frustration-plugin create separate DataExtractor instances, and each
 * subscribes to remote config independently. By sharing a single engine across
 * all extractors, whichever subscription fires first updates the engine for
 * everyone, eliminating the window where one plugin could see updated config
 * while the other still uses defaults.
 */
let sharedSelectorEngine: SelectorEngine | undefined;

function getSharedSelectorEngine(): SelectorEngine {
  if (!sharedSelectorEngine) {
    sharedSelectorEngine = createSelectorEngine(resolveSelectorConfig());
  }
  return sharedSelectorEngine;
}

export class DataExtractor {
  private readonly additionalMaskTextPatterns: RegExp[];
  diagnosticsClient?: IDiagnosticsClient;

  /**
   * Shared element-selector engine. This is the single place autocapture turns
   * an element into a selector string ({@link getElementPath}), so it's the
   * seam where the legacy `cssPath` walker is swapped for the configurable
   * engine. It ships dormant: with the default config (`enabled: false`) the
   * engine routes through the byte-identical legacy walker, so behavior is
   * unchanged until remote config flips an org onto the new algorithm via
   * {@link updateSelectorConfig}.
   *
   * The engine is shared across all DataExtractor instances to ensure
   * consistent selector output when both autocapture-plugin and
   * frustration-plugin are active.
   */
  private readonly selectorEngine: SelectorEngine;

  constructor(options: ElementInteractionsOptions, context?: { diagnosticsClient: IDiagnosticsClient }) {
    this.diagnosticsClient = context?.diagnosticsClient;
    this.selectorEngine = getSharedSelectorEngine();

    const rawPatterns = options.maskTextRegex ?? [];

    const compiled: RegExp[] = [];
    for (const entry of rawPatterns) {
      if (compiled.length >= constants.MAX_MASK_TEXT_PATTERNS) {
        break;
      }
      if (entry instanceof RegExp) {
        compiled.push(entry);
      } else if ('pattern' in entry && typeof entry.pattern === 'string') {
        try {
          compiled.push(new RegExp(entry.pattern, 'i'));
        } catch {
          // ignore invalid pattern strings
        }
      }
    }
    this.additionalMaskTextPatterns = compiled;
  }

  /**
   * Wrapper method to replace sensitive strings using the helper function
   * @param text - The text to search for sensitive data
   * @returns The text with sensitive data replaced by masked text
   */
  replaceSensitiveString = (text: string | null): string => {
    return replaceSensitiveString(text, this.additionalMaskTextPatterns);
  };

  // Get the DOM hierarchy of the element, starting from the target element to the root element.
  getHierarchy = (element: Element | null): Hierarchy => {
    const startTime = performance.now();

    let hierarchy: Hierarchy = [];
    if (!element) {
      return [];
    }

    // Get list of ancestors including itself and get properties at each level in the hierarchy
    const ancestors = getAncestors(element);

    // Build attributes to mask map
    const elementToAttributesToMaskMap = new Map<Element, Set<string>>();

    for (let i = ancestors.length - 1; i >= 0; i--) {
      const node = ancestors[i];
      if (node) {
        const attributesToMask = parseAttributesToMask(node.getAttribute(constants.DATA_AMP_MASK_ATTRIBUTES));
        const ancestorAttributesToMask =
          i === ancestors.length - 1 ? [] : elementToAttributesToMaskMap.get(ancestors[i + 1]) ?? new Set<string>();
        const combinedAttributesToMask = new Set([...ancestorAttributesToMask, ...attributesToMask]);
        elementToAttributesToMaskMap.set(node, combinedAttributesToMask);
      }
    }

    hierarchy = ancestors.map((el) =>
      getElementProperties(el, elementToAttributesToMaskMap.get(el) ?? new Set<string>()),
    );

    // Search for and mask any sensitive attribute values
    for (const hierarchyNode of hierarchy) {
      if (hierarchyNode?.attrs) {
        Object.entries(hierarchyNode.attrs).forEach(([key, value]) => {
          if (hierarchyNode.attrs) {
            hierarchyNode.attrs[key] = this.replaceSensitiveString(value);
          }
        });
      }
    }

    const endTime = performance.now();
    this.diagnosticsClient?.recordHistogram('autocapturePlugin.getHierarchy', endTime - startTime);

    return hierarchy;
  };

  getNearestLabel = (element: Element): string => {
    const parent = element.parentElement;
    if (!parent) {
      return '';
    }
    let labelElement: Element | null;
    try {
      labelElement = parent.querySelector(':scope>span,h1,h2,h3,h4,h5,h6');
    } catch {
      /* istanbul ignore next */
      labelElement = null;
    }
    if (labelElement) {
      /* istanbul ignore next */
      return this.getText(labelElement);
    }
    return this.getNearestLabel(parent);
  };

  getElementPath = (element: Element | null): string => {
    if (!element) {
      return '';
    }
    const startTime = performance.now();

    const elementPath = this.selectorEngine.generate(element);

    const endTime = performance.now();
    this.diagnosticsClient?.recordHistogram('autocapturePlugin.getElementPath', endTime - startTime);

    return elementPath;
  };

  /**
   * Apply an element-selector remote-config payload to the engine. Called from
   * plugin setup when remote config is delivered.
   *
   * The engine ships dormant: it is constructed with the resolved defaults
   * (`enabled: false`), so a client that never receives a payload stays on the
   * legacy `cssPath`. Delivering `{ enabled: true }` switches
   * {@link getElementPath} onto the new strategy-based output; an explicit
   * `{ enabled: false }` switches it back.
   *
   * A delivery is applied only when it explicitly carries a boolean `enabled`.
   * Anything else — `null`/absent (failed or key-less remote fetch), an empty
   * `{}`, or a partial payload without `enabled` — is treated as "no update"
   * and ignored rather than reset to defaults. In `'all'` delivery mode the
   * client delivers cache-then-remote (and both autocapture and frustration
   * subscribe), so incomplete deliveries are common; applying them would default
   * `enabled` to `false` via {@link resolveSelectorConfig} and silently tear down
   * a live engine (enable, then disable) — the flip-flop this guard prevents.
   *
   * Off-by-default is unaffected: the engine still boots disabled, and only an
   * explicit `enabled: true` ever turns it on. The trade-off is that disabling
   * must be done with an explicit `enabled: false`, not by removing the config
   * or sending a partial payload.
   */
  updateSelectorConfig = (remote?: ElementSelectorRemoteConfig | null, logger?: ElementSelectorLogger): void => {
    // No-op unless the delivery explicitly specifies a boolean `enabled`. This
    // covers null/undefined AND non-null-but-incomplete payloads ({} or partial
    // configs), both of which would otherwise resolve to disabled and revert a
    // previously-enabled engine to the legacy cssPath.
    if (remote === null || remote === undefined || typeof remote.enabled !== 'boolean') {
      logger?.debug(
        '@amplitude/element-selector: ignoring remote-config delivery without an explicit `enabled` flag — keeping current engine state.',
      );
      return;
    }

    const wasEnabled = this.selectorEngine.getConfig().enabled;
    const resolved = resolveSelectorConfig(remote, logger);
    this.selectorEngine.updateConfig(resolved);

    // Log once, on the transition from the dormant legacy walker to the new
    // strategy-based engine. Because the engine is shared across all extractors,
    // comparing the prior config against the resolved one means this fires a
    // single time when an org first gets flipped on — not on every remote-config
    // re-delivery or for the second plugin sharing the same engine.
    if (!wasEnabled && resolved.enabled) {
      logger?.debug(
        '@amplitude/element-selector: engine enabled — now emitting new element-selector element paths for autocapture events.',
      );
    } else if (wasEnabled && !resolved.enabled) {
      // Surfaces a silent re-disable: the subscription runs in 'all' mode and can
      // fire multiple times; a later payload without a boolean `enabled` resolves
      // to disabled, reverting getElementPath to the legacy cssPath with no other
      // signal. Logging it turns that invisible flip into an observable event.
      logger?.debug('@amplitude/element-selector: engine disabled — reverting to legacy cssPath for element paths.');
    }
  };

  // Returns the Amplitude event properties for the given element.
  getEventProperties = (actionType: ActionType, element: Element, dataAttributePrefix: string) => {
    /* istanbul ignore next */
    const tag = element?.tagName?.toLowerCase?.();
    /* istanbul ignore next */
    const rect =
      typeof element.getBoundingClientRect === 'function' ? element.getBoundingClientRect() : { left: null, top: null };

    const hierarchy = this.getHierarchy(element);
    const currentElementAttributes = hierarchy[0]?.attrs;
    const nearestLabel = this.getNearestLabel(element);
    const attributes = extractPrefixedAttributes(currentElementAttributes ?? {}, dataAttributePrefix);

    /* istanbul ignore next */
    const properties: Record<string, any> = {
      [constants.AMPLITUDE_EVENT_PROP_ELEMENT_HIERARCHY]: hierarchy,
      [constants.AMPLITUDE_EVENT_PROP_ELEMENT_TAG]: tag,
      [constants.AMPLITUDE_EVENT_PROP_ELEMENT_TEXT]: this.getText(element),
      [constants.AMPLITUDE_EVENT_PROP_ELEMENT_POSITION_LEFT]: rect.left == null ? null : Math.round(rect.left),
      [constants.AMPLITUDE_EVENT_PROP_ELEMENT_POSITION_TOP]: rect.top == null ? null : Math.round(rect.top),
      [constants.AMPLITUDE_EVENT_PROP_ELEMENT_ATTRIBUTES]: attributes,
      [constants.AMPLITUDE_EVENT_PROP_ELEMENT_PATH]: this.getElementPath(element),
      [constants.AMPLITUDE_EVENT_PROP_ELEMENT_PARENT_LABEL]: nearestLabel,
      [constants.AMPLITUDE_EVENT_PROP_PAGE_URL]: getDecodeURI(window.location.href.split('?')[0]),
      [constants.AMPLITUDE_EVENT_PROP_PAGE_TITLE]: (
        getPageTitle as (parseTitleFunction: (title: string) => string) => string
      )(this.replaceSensitiveString),
      [constants.AMPLITUDE_EVENT_PROP_VIEWPORT_HEIGHT]: window.innerHeight,
      [constants.AMPLITUDE_EVENT_PROP_VIEWPORT_WIDTH]: window.innerWidth,
    };

    const pageViewId = getCurrentPageViewId();
    /* istanbul ignore next */
    if (pageViewId) {
      /* istanbul ignore next */
      properties[constants.AMPLITUDE_EVENT_PROP_PAGE_VIEW_ID] = pageViewId;
    }

    // id is never masked, so always include it
    properties[constants.AMPLITUDE_EVENT_PROP_ELEMENT_ID] = element.getAttribute('id') || '';

    // class is never masked, so always include it
    properties[constants.AMPLITUDE_EVENT_PROP_ELEMENT_CLASS] = element.getAttribute('class');

    properties[constants.AMPLITUDE_EVENT_PROP_ELEMENT_ARIA_LABEL] = currentElementAttributes?.['aria-label'];

    if (tag === 'a' && actionType === 'click' && element instanceof HTMLAnchorElement) {
      const href = element.href.substring(0, constants.MAX_ATTRIBUTE_LENGTH);
      properties[constants.AMPLITUDE_EVENT_PROP_ELEMENT_HREF] = this.replaceSensitiveString(href); // we don't use hierarchy here because we don't want href value to be changed
    }

    return removeEmptyProperties(properties);
  };

  addTypeAndTimestamp = <T>(
    event: T,
    type: BaseTimestampedEvent<T>['type'] | ElementBasedTimestampedEvent<T>['type'],
  ): BaseTimestampedEvent<T> | ElementBasedTimestampedEvent<T> => {
    return {
      event,
      timestamp: Date.now(),
      type,
    };
  };

  addAdditionalEventProperties = <T>(
    event: T,
    type: TimestampedEvent<T>['type'],
    selectorAllowlist: string[],
    dataAttributePrefix: string,
    // capture the event if the cursor is a "pointer" when this element is clicked on
    // reason: a "pointer" cursor indicates that an element should be interactable
    //         regardless of the element's tag name
    isCapturingCursorPointer = false,
  ): TimestampedEvent<T> | ElementBasedTimestampedEvent<T> => {
    const baseEvent = this.addTypeAndTimestamp(event, type);

    if (isElementBasedEvent(baseEvent) && baseEvent.event.target !== null) {
      if (isCapturingCursorPointer) {
        const isCursorPointer = isElementPointerCursor(baseEvent.event.target as Element, baseEvent.type);
        if (isCursorPointer) {
          baseEvent.closestTrackedAncestor = baseEvent.event.target as HTMLElement;
          baseEvent.targetElementProperties = this.getEventProperties(
            baseEvent.type,
            baseEvent.closestTrackedAncestor,
            dataAttributePrefix,
          );
          return baseEvent;
        }
      }
      // Retrieve additional event properties from the target element
      const closestTrackedAncestor = getClosestElement(baseEvent.event.target as HTMLElement, selectorAllowlist);
      if (closestTrackedAncestor) {
        baseEvent.closestTrackedAncestor = closestTrackedAncestor;
        baseEvent.targetElementProperties = this.getEventProperties(
          baseEvent.type,
          closestTrackedAncestor,
          dataAttributePrefix,
        );
      }
      return baseEvent;
    }

    return baseEvent;
  };

  extractDataFromDataSource = (dataSource: DataSource, contextElement: HTMLElement) => {
    // Extract from DOM Element
    if (dataSource.sourceType === 'DOM_ELEMENT') {
      const sourceElement = getDataSource(dataSource, contextElement);
      if (!sourceElement) {
        return undefined;
      }

      if (dataSource.elementExtractType === 'TEXT') {
        return this.getText(sourceElement);
      } else if (dataSource.elementExtractType === 'ATTRIBUTE' && dataSource.attribute) {
        return sourceElement.getAttribute(dataSource.attribute);
      }
      return undefined;
    }

    // TODO: Extract from other source types
    return undefined;
  };

  // Traverse text content without cloning DOM nodes, which avoids media/network side effects
  // from recreating nested elements such as <video>, <audio>, or <img>.
  private getTextWithMaskedDescendants = (element: Element): string => {
    const maskedSelector = `[${TEXT_MASK_ATTRIBUTE}], [contenteditable]`;
    // Fast path: if no masked descendants exist, rely on native text extraction.
    if (!element.querySelector(maskedSelector)) {
      return (element as HTMLElement).innerText;
    }

    let output = '';
    const childNodes = Array.from(element.childNodes);
    for (const childNode of childNodes) {
      if (childNode.nodeType === Node.TEXT_NODE) {
        output += childNode.textContent || '';
        continue;
      }

      if (!(childNode instanceof Element)) {
        continue;
      }

      // Replace entire masked/contenteditable subtrees with the mask token.
      if (childNode.hasAttribute(TEXT_MASK_ATTRIBUTE) || childNode.hasAttribute('contenteditable')) {
        output += MASKED_TEXT_VALUE;
        continue;
      }
      output += this.getTextWithMaskedDescendants(childNode);
    }
    return output;
  };

  getText = (element: Element): string => {
    // Check if element or any parent has data-amp-mask attribute
    const hasMaskAttribute = element.closest(`[${TEXT_MASK_ATTRIBUTE}]`) !== null;
    if (hasMaskAttribute) {
      return MASKED_TEXT_VALUE;
    }
    let output = '';
    if (!element.querySelector(`[${TEXT_MASK_ATTRIBUTE}], [contenteditable]`)) {
      output = (element as HTMLElement).innerText || '';
    } else {
      output = this.getTextWithMaskedDescendants(element);
    }
    return this.replaceSensitiveString(output.substring(0, 255)).replace(/\s+/g, ' ').trim();
  };

  // Returns the element properties for the given element in Visual Labeling.
  getEventTagProps = (element: Element): Record<string, JSONValue> => {
    if (!element) {
      return {};
    }
    /* istanbul ignore next */
    const tag = element?.tagName?.toLowerCase?.();

    const properties = {
      [constants.AMPLITUDE_EVENT_PROP_ELEMENT_TAG]: tag,
      [constants.AMPLITUDE_EVENT_PROP_ELEMENT_TEXT]: this.getText(element),
      [constants.AMPLITUDE_EVENT_PROP_PAGE_URL]: window.location.href.split('?')[0],
    };
    return removeEmptyProperties(properties) as Record<string, JSONValue>;
  };
}
